import React from "react";

import { HiddenTooltipProvider, useIsTooltipHidden, useTooltipConfig } from "./context";

const TooltipRoot = React.lazy(async () => {
	const Tooltip = await import("@radix-ui/react-tooltip");

	return { default: Tooltip.Root };
});

const TooltipProvider = React.lazy(async () => {
	const Tooltip = await import("@radix-ui/react-tooltip");

	return { default: Tooltip.Provider };
});

export interface BoundaryProps extends React.ComponentPropsWithoutRef<typeof TooltipRoot> {
	/**
	 * Hides the tooltip content when set to `true`.
	 * When set to `true`, only the trigger element is rendered, and the tooltip content is not shown.
	 *
	 * @default false
	 */
	hide?: boolean;
}

export const Boundary: React.FC<BoundaryProps> = ({ hide = false, children, ...props }) => {
	const nested = useIsTooltipHidden();
	const config = useTooltipConfig();
	const fallback = <HiddenTooltipProvider value>{children}</HiddenTooltipProvider>;

	if (hide || nested) {
		return fallback;
	}

	return (
		<React.Suspense fallback={fallback}>
			<TooltipProvider {...config}>
				<TooltipRoot {...props}>{children}</TooltipRoot>
			</TooltipProvider>
		</React.Suspense>
	);
};

Boundary.displayName = "Tooltip.Boundary";
